import * as moment from 'moment-timezone'
import { BASE_TIME_ONLY_FORMAT, BASE_TIME_ONLY_FORMAT_WITHOUT_TZ } from '../../../constants'

/** The zone codes a scheduled time can carry instead of an IANA zone: the subscriber's own, or informed delivery. */
export type ScheduledTimeZoneCode = 'STZ' | 'INF'

export type ScheduledTimeDisplay = {
    /** The moment in the zone it was scheduled for, so its date is the date in that zone. */
    timestamp: ReturnType<typeof moment.utc>
    /** The time of day, with the zone abbreviation when the zone is an IANA zone. */
    timeText: string
    /** Set when the zone is one of the codes, which have no abbreviation of their own. */
    zoneCode: ScheduledTimeZoneCode | null
}

/**
 * How a notification's scheduled time is read. An `STZ` or `INF` time is a wall-clock time with no single zone,
 * so it is shown as that time with the code beside it; any other zone is an IANA zone and the time is read in
 * it. A time with no zone is read as UTC, which `moment.tz` does for an undefined zone.
 */
export const scheduledTimeDisplay = (
    scheduledTime: string,
    scheduledTimeZone: string | null | undefined,
): ScheduledTimeDisplay => {
    if (scheduledTimeZone === 'STZ' || scheduledTimeZone === 'INF') {
        const wallClock = moment.utc(scheduledTime)
        return {
            timestamp: wallClock,
            timeText: wallClock.format(BASE_TIME_ONLY_FORMAT_WITHOUT_TZ),
            zoneCode: scheduledTimeZone,
        }
    }
    const zoned = moment.tz(scheduledTime, scheduledTimeZone)
    return { timestamp: zoned, timeText: zoned.format(BASE_TIME_ONLY_FORMAT), zoneCode: null }
}
